/**
 * State 04 — Sign in: Set a new password (after forgot-password flow).
 *
 * Triggered from state 03 (forgotPassword) where we called
 * `signIn.create({ strategy: "reset_password_email_code", identifier })`.
 * Here we collect the 6-digit code + a new password, and submit:
 *
 *   signIn.attemptFirstFactor({
 *     strategy: "reset_password_email_code",
 *     code,
 *     password,
 *   })
 *
 * Clerk verifies the code, updates the password, and returns
 * status === "complete" with a createdSessionId on success.
 *
 * Mockup state 04: OTP, resend countdown, new password input with
 * eye toggle, 4-segment strength meter, hint, "Save and sign in"
 * CTA, "Back to sign in" secondary.
 */

"use client";

import { useMemo, useState } from "react";

import { useSignIn } from "@clerk/nextjs";

import { activateSession } from "./sessionActivation";

import {
  IconEye,
  IconEyeOff,
  IconWarningTriangle,
} from "./AuthIcons";
import { EllipsisDots } from "./EllipsisDots";
import { IdentityRow } from "./IdentityRow";
import { OtpInput } from "./OtpInput";
import { passwordStrength } from "./passwordStrength";
import { useResendCountdown } from "./useResendCountdown";
import {
  AuthFlowState,
  AuthStep,
  readClerkError,
} from "./useAuthFlow";
import { trackSignupCompleted } from "@/lib/analytics";

type Props = {
  flow: AuthFlowState;
  setError: (e: string | null) => void;
  setSubmitting: (b: boolean) => void;
  markResend: () => void;
  goTo: (s: AuthStep) => void;
  onAuthenticated: () => void;
};

export function SignInResetPasswordCard({
  flow,
  setError,
  setSubmitting,
  markResend,
  goTo,
  onAuthenticated,
}: Props) {
  const { signIn, setActive, isLoaded } = useSignIn();
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const resend = useResendCountdown(flow.lastResendAt);
  const strength = useMemo(() => passwordStrength(password), [password]);

  async function attempt() {
    if (!isLoaded || !signIn || !setActive) return;
    if (code.length !== 6 || password.length < 8) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code,
        password,
      });
      if (result.status === "complete" && result.createdSessionId) {
        // 2026-08-29: bounded activation (sessionActivation.ts).
        await activateSession({
          setActive,
          sessionId: result.createdSessionId,
        });
        // C21: through the sign-up gate. Attribution (negotiate vs
        // elsewhere) comes from the marker written when the gate was raised.
        trackSignupCompleted({ method: "sign_in" });
        onAuthenticated();
        return;
      }
      if (result.status === "needs_new_password") {
        setError("Choose a stronger password and try again.");
        return;
      }
      setError("Could not reset password. Try again.");
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    if (!isLoaded || !signIn) return;
    if (resend.disabled) return;
    setError(null);
    setSubmitting(true);
    try {
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: flow.identifier,
      });
      markResend();
    } catch (err) {
      setError(readClerkError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void attempt();
      }}
    >
      <div className="mio-auth-card-header">
        <h1>Set a new password</h1>
        <p>Enter the code we emailed and choose a new password.</p>
      </div>

      {flow.error ? (
        <div className="mio-auth-error" role="alert">
          <IconWarningTriangle />
          <div>{flow.error}</div>
        </div>
      ) : null}

      <IdentityRow
        value={flow.identifier}
        onEdit={() => goTo("signIn.forgotPassword")}
      />

      <div className="mio-auth-otp-wrap">
        <OtpInput
          value={code}
          onChange={setCode}
          disabled={flow.submitting}
        />
        <div className="mio-auth-resend">
          Didn&rsquo;t receive a code?{" "}
          <button
            type="button"
            onClick={() => void onResend()}
            disabled={resend.disabled || flow.submitting}
          >
            Resend
          </button>
          {resend.disabled ? (
            <span className="mio-auth-countdown">{resend.label}</span>
          ) : null}
        </div>
      </div>

      <div className="mio-auth-field">
        <div className="mio-auth-field-head">
          <label className="mio-auth-field-label" htmlFor="mio-rp-new">
            New password
          </label>
        </div>
        <div className="mio-auth-input-wrap">
          <input
            id="mio-rp-new"
            className="mio-auth-input with-eye"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            className="mio-auth-input-eye"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={-1}
          >
            {showPassword ? <IconEyeOff /> : <IconEye />}
          </button>
        </div>
        <div className="mio-auth-pw-meter" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={i < strength ? "on" : ""} />
          ))}
        </div>
        <div className="mio-auth-pw-hint">
          Use 12+ characters with a mix of letters, numbers and symbols.
        </div>
      </div>

      <button
        type="submit"
        className="mio-auth-cta"
        aria-busy={flow.submitting}
        disabled={
          flow.submitting || code.length !== 6 || password.length < 8
        }
      >
        {flow.submitting ? (
          <>
            Saving
            <EllipsisDots />
          </>
        ) : (
          "Save and sign in"
        )}
      </button>
      <button
        type="button"
        className="mio-auth-secondary"
        disabled={flow.submitting}
        onClick={() => goTo("signIn.start")}
      >
        Back to sign in
      </button>
    </form>
  );
}
